import { useEffect, useRef } from 'react'
import { useScrollIntoViewOnMount } from '../labs/useScrollIntoView'

// Instant-solve retrospective ("aha" payoff) — shown INSTEAD of the passive reflection when a
// solve was trivial (fast / near-wordless; see trivialSolve.ts). It's active and untimed: the mic
// stays hot, we solicit the decode ("how does it work — what did you see?") because even an instant
// solve reveals what the player spotted, and we take a one-tap self-report of whether it felt like
// insight or working-out. The spoken answer flows into the puzzle's live "What we're hearing" box
// above (this renders as a stage child) — tagged `segment:'retro'` on the trace — so the card itself
// is just the prompt + self-report + Done. The parent owns the mic; this reports the tap + Done.
//
// No emoji (labs house style).

export type SolutionType = 'insight' | 'analytic' | null

// Safety net: the mic is live here and there's no countdown, so auto-finish a card left open a
// long time rather than stream indefinitely.
const AUTO_DONE_MS = 3 * 60 * 1000

export const InstantRetro = ({
  answer,
  solutionType,
  onPickType,
  onDone,
  accent = '#820458',
}: {
  answer: string // the confirmed answer (a trivial solve is auto-credited) — shown so it's clear what they got
  solutionType: SolutionType
  onPickType: (t: 'insight' | 'analytic') => void
  onDone: () => void
  accent?: string
}) => {
  const doneRef = useRef(onDone)
  doneRef.current = onDone
  useEffect(() => {
    const id = window.setTimeout(() => doneRef.current(), AUTO_DONE_MS)
    return () => window.clearTimeout(id)
  }, [])
  const scrollRef = useScrollIntoViewOnMount<HTMLDivElement>()

  const pill = (active: boolean) =>
    `flex-1 rounded-lg border py-2.5 text-sm font-semibold transition-colors ${
      active ? 'border-transparent text-white' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
    }`

  return (
    <div ref={scrollRef} className="text-center">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-400">That was quick — you got it</p>
      <div className="mt-1.5 inline-block rounded-lg bg-[#398874]/10 px-4 py-2 text-lg font-extrabold tracking-wide text-[#398874]">
        {answer}
      </div>
      <h2 className="mt-3 text-lg font-semibold text-gray-800">Nice — how does it work?</h2>
      <p className="mt-1 text-sm text-gray-600">
        Even when it comes fast, you spotted <span className="font-semibold">something</span>. Say what you saw out loud —
        it keeps showing above.
      </p>

      <div className="mt-4">
        <p className="text-sm text-gray-600">Did it just come to you, or did you work it out?</p>
        <div className="mt-2 flex gap-2">
          <button
            type="button"
            onClick={() => onPickType('insight')}
            className={pill(solutionType === 'insight')}
            style={solutionType === 'insight' ? { backgroundColor: accent } : undefined}
          >
            It just came to me
          </button>
          <button
            type="button"
            onClick={() => onPickType('analytic')}
            className={pill(solutionType === 'analytic')}
            style={solutionType === 'analytic' ? { backgroundColor: accent } : undefined}
          >
            I worked it out
          </button>
        </div>
        {solutionType === 'insight' && <p className="mt-2 text-sm text-gray-500">Even so — can you say what you spotted?</p>}
      </div>

      <button
        type="button"
        onClick={onDone}
        className="mt-5 w-full rounded-lg py-3 font-semibold text-white"
        style={{ backgroundColor: accent }}
      >
        Done
      </button>
    </div>
  )
}
